import React, { useState, useEffect } from "react";
import { TableActions } from "../../components/others/TableActions";
import { useModal } from "../../hooks";
import { handleOpenPdfReport, handleOpenPdfReportPractice } from "../../utils/files";
import { extractExamFromMetadata, extractJustExamFromMetadata, extractMoldeFromMetadata, extractPracticesFromMetadata } from "../../utils/metadata";
import LavorisService from "../../services/LavorisService";
import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';
import InfiniteScroll from "react-infinite-scroll-component";
import RefreshIcon from '@mui/icons-material/Refresh';
import { Badge, BadgeProps } from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import { DetailTurnWithDateParticular } from "../others/detailsTurnParticular";
import moment from "moment";
import { filterMoldeExamPractice } from "../../utils/filterMoldeExamPractice";
import MultiplePracticesModal from "./MultiplePracticesModal";
import InfoOffcanvas from '../../components/others/InfoOffCanvas/InfoOffCanvas';
import InfiniteTable from '../../components/others/InfiniteTableComponent/IntiniteTableComponent';
import "./Particulars.scss";
import { formatDateTimeForTable } from "../../utils/formatDate";

const VIEW_APPOINTMENTS_MODAL = "viewAppointmentsModal";
const VIEW_MULTIPLE_PRACTICES_MODAL = 'viewMultiplePracticesModal'

const ParticularsResults = () => {
  const [hasMore, setHasMore] = useState(true);
  const LavorisInstance = LavorisService.getInstance();
  const { closeModal, openModal, isActive } = useModal();
  const [data, setData] = useState<any[]>([]);
  const [loader, setLoader] = useState(false);
  const controller = new AbortController();
  const signal = controller.signal;
  const [rowData, setRowData] = useState<any>()
  const [appId, setAppId] = useState<string>("")
  const [items, setItems] = useState({
    itemsPerPage: 20,
    page: 1,
  });

  const getAllAppointmentsCall = async () => {
    setLoader(true);
    try {
      const app = await LavorisInstance.TurnServices.getAppointmentResultsOfParticularClients(
        items.page,
        items.itemsPerPage,
        signal
      )

      if (app.length === 0) {
        //showAlert("No se encontraron registros");
        setHasMore(false);
        return;
      }

      // Obtén los IDs de las citas actuales en el estado
      const existingIds = new Set(data?.map(item => item.id));

      // Filtra las nuevas citas para agregar solo las que no están en el estado actual
      const uniqueAppointments = app?.filter(appointment => !existingIds.has(appointment.id));

      // Agrega nuevos app al estado existente
      setData(prevData => {
        return [...prevData, ...uniqueAppointments];
      });
      //Incrementa la página
      setItems(prev => ({ ...prev, page: prev.page + 1 }));
    } catch (e) {
      console.log(e);
      setHasMore(false);
    } finally {
      setLoader(false);
    }
  }

  const getAllAppointmentsRefresh = async () => {
    setLoader(true);
    try {
      const app = await LavorisInstance.TurnServices.getAppointmentResultsOfParticularClients(
        1,
        20,
        signal
      )

      if (app.length === 0) {
        //showAlert("No se encontraron registros");
        setHasMore(false);
        return;
      }
      setData([]);
      setData(app);
      setItems(prev => ({ ...prev, page: prev.page + 1 }));
    } catch (e) {
      console.log(e);
      setHasMore(false);
    }
    finally {
      setLoader(false);
    }
  }

  useEffect(() => {
    getAllAppointmentsCall();
  }, [])


  useEffect(() => {
    return () => {
      controller.abort()
    };
  }, [])


  const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: '#f5f5f9',
      color: 'rgba(0, 0, 0, 0.87)',
      maxWidth: 220,
      fontSize: theme.typography.pxToRem(12),
      border: '1px solid #dadde9',
    },
  }));


  const StyledBadge = styled(Badge)<BadgeProps>(({ theme }) => ({
    '& .MuiBadge-badge': {
      right: -3,
      top: 13,
      border: `2px solid ${theme.palette.background.paper}`,
      padding: '0 4px',
      margin: '0  4px'
    },
  }));

  const returnRetiro = (rowData: any) => {
    if (!rowData?.actions || rowData?.actions?.length === 0) return '---';

    const retirado = rowData?.actions.filter((action: any) => action.action === 'RETIRADO');
    if (retirado.length === 0) return '---';
    // Asegúrate de convertir la fecha de UTC a la zona horaria correcta
    return moment(retirado[0].date).format('DD/MM/YY HH:mm');
  };


  // Funciones auxiliares fuera de actions para mejor organización
  const hasOnlyIsolatedPractices = (row: any) => {
    if (!row?.metadata) return false;
    const practices = extractPracticesFromMetadata(row.metadata);
    if (!practices) return false;

    return practices.every((p: any) => p.type === "PRACTICE" || p.type === 'PRACTICE_IN_MOLDE');
  };

  const downloadSequentiallyPDFs = async (rowData: any) => {
    const practices = extractPracticesFromMetadata(rowData.metadata);
    const patient = rowData.patient.name || rowData?.patient?.name;
    if (!practices) return false;

    for (const p of practices) {
      await handleOpenPdfReportPractice((p as any).pdfUrl, p.name, patient);
    }
  };

  const findExam = (appointment: any) => {
    if (!appointment?.metadata) return;
    return extractExamFromMetadata(appointment?.metadata);
  };

  const hasExamFullReport = (row: any) => !!findExam(row)?.fullReportUrl;

  const getExamPDF = (metadata: any) => {
    const exam = extractExamFromMetadata(metadata);
    if (!exam?.fullReportUrl) throw new Error("No PDF generated for exam");

    return handleOpenPdfReport(exam.fullReportUrl);
  };

  const actions = (row: any) => {
    // Si el status es 'Aptitud parcialmente completada', solo mostrar texto
    if (row.status === 'Aptitud parcialmente completada') {
      return 'Informe Pendiente';
    }

    // De lo contrario, mostrar los botones de acciones
    return (
      <div className="flex items-center align-middle justify-center gap-2">
        <TableActions
          type="View"
          onClick={() => openModal(VIEW_APPOINTMENTS_MODAL, row.id)}
        />
        <span
          className={`inline-flex items-center justify-center w-8 h-8 font-bold text-lg leading-none ${row.payment_done ? "text-green-500" : "text-red-500"}`}
          title={row.payment_done ? "Pago realizado" : "Pago pendiente"}
        >
          $
        </span>
        {hasExamFullReport(row) && (
          <>
            <TableActions
              type="View"
              title="Ver PDF indivualmente"
              onClick={() => {
                setRowData(row)
                setAppId(row.id)
                openModal(VIEW_MULTIPLE_PRACTICES_MODAL)
              }}
            />
            <TableActions
              type="PDF"
              title={'Descargar Informe'}
              onClick={() => getExamPDF(row.metadata)}
            />
          </>
        )}
        {hasOnlyIsolatedPractices(row) && (
          <>
            <TableActions
              type="View"
              title="Ver PDF indivualmente"
              onClick={() => {
                setRowData(row)
                setAppId(row.id)
                openModal(VIEW_MULTIPLE_PRACTICES_MODAL)
              }}
            />
            <TableActions
              type="Download"
              title="Descargar PDF múltiples"
              onClick={() => downloadSequentiallyPDFs(row)}
            />
          </>
        )}
      </div>
    );
  }

  return (
    <>

      <div className="main-container particulars-results-container">
        <div className="atendoo-card">
          <div className="custom-header">
            <div className="header-top">
              {/* <h6 className="subtitle">Solicitudes de Turnos</h6> */}
              <InfoOffcanvas title="INFORMACIÓN DE TABLA">
                <ul className="text-md p-auto m-auto">
                  <li>- A medida que se va haciendo scroll para abajo, la tabla va buscando más resultados.</li>
                </ul>
              </InfoOffcanvas>
            </div>
            <div className="header-controls">
              <div className='d-flex gap-2'>


                {/* Results Count */}
                <div className="results-count">
                  <span className="count-number">{data.length}</span>
                  <span className="count-label">resultados</span>
                </div>

                {/* Refresh Button */}
                <button
                  className="refresh-btn"
                  onClick={getAllAppointmentsRefresh}
                  title="Actualizar tabla"
                  disabled={loader}
                >
                  {loader ? (
                    <div className="loading-spinner">
                      <div className="spinner"></div>
                    </div>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M13.65 2.35C12.2 0.9 10.21 0 8 0 3.58 0 0 3.58 0 8s3.58 8 8 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L9 5h7V-2l-2.35 2.35z"
                        fill="currentColor"
                      />
                    </svg>
                  )}
                  <span className="btn-text">Actualizar</span>
                </button>
              </div>


            </div>
          </div>

          <InfiniteTable
            data={data}
            columns={[
              {
                key: 'fecha',
                title: 'Fecha de Solicitud',
                className: 'col-fecha-solicitud',
                expandable: false,
                render: (value: any, row: any) => (
                  moment(row.createdDate).format('DD/MM/YYYY HH:mm') || "---"
                )
              },
              {
                key: 'fecha_turno',
                title: 'Fecha y Hora del Turno',
                className: 'col-fecha-turno',
                render: (value: any, row: any) => formatDateTimeForTable(row?.date, row?.startTime)
              },
              {
                key: 'fecha_admision',
                title: 'Fecha y Hora de Admisión',
                className: 'col-fecha-admision',
                render: (value: any, row: any) => (
                  row.admissionTime ? row.admissionTime : '---'
                )
              },
              {
                key: 'fecha_retiro',
                title: 'Fecha y Hora de Retiro',
                className: 'col-fecha-retiro',
                render: (value: any, row: any) => (
                  returnRetiro(row)
                )
              },
              {
                key: 'descripcion',
                title: 'Descripción',
                className: 'col-descripcion',
                render: (value: any, row: any) => (
                  filterMoldeExamPractice(row) || "---"
                )
              },
              {
                key: 'acciones',
                title: 'Acciones',
                className: 'col-acciones',
                render: (value: any, row: any) => (
                  actions(row) || "---"
                )
              }
            ]}
            loading={loader}
            hasMore={hasMore}
            onLoadMore={getAllAppointmentsCall}
            height={400}
          />
        </div>
      </div>





      <div className="py-3 bg-white">


        <div>
          {/* <div className="flex justify-between p-2 border-b bg-slate-300">
            <div className="w-1/5 flex justify-center align-middle text-center font-bold">Fecha de solicitud</div>
            <div className="w-1/5 flex justify-center align-middle text-center font-bold">Fecha y hora del turno</div>
            <div className="w-1/5 flex justify-center align-middle text-center font-bold">Fecha y hora de admisión</div>
            <div className="w-1/5 flex justify-center align-middle text-center font-bold">Fecha y hora de retiro</div>
            <div className="w-1/5 flex justify-center align-middle text-center font-bold">Descripción</div>
            <div className="w-1/5 flex justify-center align-middle text-center font-bold">Acciones</div>
          </div> */}

          {/* <InfiniteScroll
            className="border"
            dataLength={data.length}
            next={getAllAppointmentsCall}
            hasMore={hasMore}
            loader={() => { }}
            height={570}
          >
            {data.length === 0 && (
              <div className="flex justify-center align-middle my-5 text-lg text-red-600">
                Momentaneamente no hay registros cargados para mostrar.
              </div>
            )}
            {data.map((rowData: any) => (
              <div key={rowData.id} className="flex justify-between p-2 border-b">
                <div className="w-1/5 flex justify-center align-middle">
                  {moment(rowData.createdDate).format('DD/MM/YYYY HH:mm') || "---"}
                </div>
                <div className="w-1/5 flex justify-center align-middle">
                  {rowData.date && rowData.startTime ? `${rowData.date} - ${rowData.startTime}` : "---"}
                </div>
                <div className="w-1/5 flex justify-center align-middle">
                  {`${rowData.admissionTime ? rowData.admissionTime : '---'}`}
                </div>
                <div className="w-1/5 flex justify-center text-center text-sm">
                  {returnRetiro(rowData)}
                </div>
                <div className="w-1/5 flex justify-end align-middle">
                  {filterMoldeExamPractice(rowData) || "---"}
                </div>
                <div className="w-1/5 flex gap-2 justify-center">
                  {actions(rowData) || "---"}
                </div>
              </div>
            ))}



          </InfiniteScroll> */}

        </div>

        {
          isActive(VIEW_APPOINTMENTS_MODAL) && (
            <DetailTurnWithDateParticular
              show
              modalTitle={"Detalle de turno"}
              urlTittle={VIEW_APPOINTMENTS_MODAL}
              closeModal={() => closeModal(VIEW_APPOINTMENTS_MODAL)}
            />
          )
        }

        {isActive(VIEW_MULTIPLE_PRACTICES_MODAL) && (
          <MultiplePracticesModal
            rowData={rowData}
            appId={appId}
            show
            refreshTable={() => { }}
            onClose={() => closeModal(VIEW_MULTIPLE_PRACTICES_MODAL)}
            modalTitle={"Descargar prácticas"}
            urlTittle={VIEW_MULTIPLE_PRACTICES_MODAL}
            download={handleOpenPdfReport}
          />
        )}

      </div>
    </>
  )
}

export default ParticularsResults;
